import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { CommentDto, Country } from "../../../../../../api/Api";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useCountryAssignees } from "../../../../dataHooks/countryQueries";
import { countryAttachmentApi } from "../../../countryAttachmentApi";
import { useCountryLifecycle } from "../../useCountryLifecycle";

type Props = {
  specificCountry: Country;
};

type FormData = {
  marketAccessAssignee: string;
  localBranch: string;
  localBranchName: string;
  contractingEntityName: string;
  tradingLicense: string;
  licenseAuthorityName: string;
  legalConsultancy: string;
  legalConsultantsName: string;
  legalConsultantsContactName: string;
  legalConsultantsContactEmail: string;
  tso: string;
  tsoName: string;
  exchange: string;
  exchangeName: string;
  bank: string;
  bankName: string;
  clearingBank: string;
  clearingBankName: string;
  contractualMftTeam: string;
  ndaReview: string;
  mandate: string;
  collateral: string;
  tradeCapture: string;
  userSetup: string;
  signOffNote: string;
  comments: CommentDto[];
};

const NEEDED_OPTIONS = [
  { value: "needed", label: "Needed" },
  { value: "not_needed", label: "Not needed" }
];

/**
 * Market Access owns the shape of a country setup: the needed/not-needed toggles here decide which
 * parties exist, and that decision alone drives every other lifecycle. Nothing downstream invents a
 * party of its own, and nothing here narrows which department sees which party. An earlier version
 * had pickers for that and they were redundant, see docs/country-additions-plan.md.
 */
export const MarketAccess: FC<Props> = ({ specificCountry }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const lifecycle = specificCountry.marketAccessLifecycle;

  const [formData, setFormData] = useState<FormData>({
    marketAccessAssignee: lifecycle?.marketAccessAssignee || "",
    localBranch: lifecycle?.localBranch || "",
    localBranchName: lifecycle?.localBranchName || "",
    contractingEntityName: lifecycle?.contractingEntityName || "",
    tradingLicense: lifecycle?.tradingLicense || "",
    licenseAuthorityName: lifecycle?.licenseAuthorityName || "",
    legalConsultancy: lifecycle?.legalConsultancy || "",
    legalConsultantsName: lifecycle?.legalConsultantsName || "",
    legalConsultantsContactName: lifecycle?.legalConsultantsContactName || "",
    legalConsultantsContactEmail: lifecycle?.legalConsultantsContactEmail || "",
    tso: lifecycle?.tso || "",
    tsoName: lifecycle?.tsoName || "",
    exchange: lifecycle?.exchange || "",
    exchangeName: lifecycle?.exchangeName || "",
    bank: lifecycle?.bank || "",
    bankName: lifecycle?.bankName || "",
    clearingBank: lifecycle?.clearingBank || "",
    clearingBankName: lifecycle?.clearingBankName || "",
    contractualMftTeam: lifecycle?.contractualMftTeam || "",
    ndaReview: lifecycle?.ndaReview || "",
    mandate: lifecycle?.mandate || "",
    collateral: lifecycle?.collateral || "",
    tradeCapture: lifecycle?.tradeCapture || "",
    userSetup: lifecycle?.userSetup || "",
    signOffNote: lifecycle?.signOffNote || "",
    comments: lifecycle?.comments || []
  });

  const handleClose = () => navigate(`/countries/country/${params.id}`);

  const {
    handleSave,
    handleComplete,
    isSaving,
    progressOverride,
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment
  } = useCountryLifecycle({
    country: specificCountry,
    lifecycle: "marketAccessLifecycle",
    formData,
    setFormData,
    onClose: handleClose
  });

  const marketAccessAssignees = useCountryAssignees("marketAccess");

  const handleChange = <TField extends keyof FormData>(
    field: TField,
    value: FormData[TField]
  ) => setFormData((prev) => ({ ...prev, [field]: value }));

  const open = location.pathname.includes("marketaccess");

  // Everything that decides whether another department has work to do. The names are deliberately
  // not required: they are often filled in after the decision, and a missing name does not stop
  // Compliance or Legal starting.
  const canComplete = Boolean(
    formData.marketAccessAssignee &&
      formData.localBranch &&
      formData.tradingLicense &&
      formData.legalConsultancy &&
      formData.tso &&
      formData.exchange &&
      formData.bank &&
      formData.clearingBank &&
      formData.ndaReview &&
      formData.mandate &&
      formData.collateral &&
      formData.tradeCapture &&
      formData.userSetup
  );

  const neededSelect = (
    label: string,
    field: keyof FormData,
    nameField?: keyof FormData,
    nameLabel?: string
  ) => (
    <Box display="flex" gap={2}>
      <FormControl size="medium" required sx={{ flex: 1 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={formData[field] as string}
          onChange={(e) => handleChange(field, e.target.value as never)}
        >
          {NEEDED_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {nameField && formData[field] === "needed" && (
        <TextField
          sx={{ flex: 1 }}
          label={nameLabel}
          value={formData[nameField] as string}
          onChange={(e) => handleChange(nameField, e.target.value as never)}
        />
      )}
    </Box>
  );

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Market Access"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={countryAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: () => handleSave(),
        onComplete: () => handleComplete(),
        canComplete,
        isSaving
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Autocomplete
          options={marketAccessAssignees.data || []}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          value={
            marketAccessAssignees.data?.find(
              (a) => a.email === formData.marketAccessAssignee
            ) || null
          }
          onChange={(_, newValue) =>
            handleChange("marketAccessAssignee", newValue?.email || "")
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Market access assignee"
              size="medium"
            />
          )}
        />

        <Typography variant="subtitle1" fontWeight={600}>
          Parties this country setup needs
        </Typography>

        {neededSelect(
          "Local branch",
          "localBranch",
          "localBranchName",
          "Local branch name"
        )}
        {formData.localBranch === "not_needed" && (
          <TextField
            label="Contracting entity name"
            value={formData.contractingEntityName}
            onChange={(e) =>
              handleChange("contractingEntityName", e.target.value)
            }
            helperText="Which entity contracts instead of a local branch."
          />
        )}

        {neededSelect(
          "Trading license",
          "tradingLicense",
          "licenseAuthorityName",
          "License authority name"
        )}

        {neededSelect(
          "Legal consultancy",
          "legalConsultancy",
          "legalConsultantsName",
          "Consultants name"
        )}
        {formData.legalConsultancy === "needed" && (
          <Box display="flex" gap={2}>
            <TextField
              sx={{ flex: 1 }}
              label="Consultants contact name"
              value={formData.legalConsultantsContactName}
              onChange={(e) =>
                handleChange("legalConsultantsContactName", e.target.value)
              }
            />
            <TextField
              sx={{ flex: 1 }}
              label="Consultants contact email"
              value={formData.legalConsultantsContactEmail}
              onChange={(e) =>
                handleChange("legalConsultantsContactEmail", e.target.value)
              }
            />
          </Box>
        )}

        {neededSelect("TSO", "tso", "tsoName", "TSO name")}
        {neededSelect("Exchange", "exchange", "exchangeName", "Exchange name")}
        {neededSelect("Bank", "bank", "bankName", "Bank name")}
        {neededSelect(
          "Clearing bank",
          "clearingBank",
          "clearingBankName",
          "Clearing bank name"
        )}

        <TextField
          label="Contractual MFT team"
          value={formData.contractualMftTeam}
          onChange={(e) => handleChange("contractualMftTeam", e.target.value)}
        />

        <Divider />

        <Typography variant="subtitle1" fontWeight={600}>
          Other decisions
        </Typography>

        <Alert severity="info" variant="outlined">
          Every party marked needed above automatically gets a KYC check and
          sanction screening on Compliance, an agreement review on Legal, and a
          setup row on Finance. Those departments mark a row not needed
          themselves if it turns out not to apply.
        </Alert>

        {neededSelect("NDA review", "ndaReview")}
        {neededSelect("Mandate", "mandate")}
        {neededSelect("Collateral", "collateral")}
        {neededSelect("Trade capture", "tradeCapture")}

        <FormControl size="medium" required>
          <InputLabel>User setup</InputLabel>
          <Select
            label="User setup"
            value={formData.userSetup}
            onChange={(e) => handleChange("userSetup", e.target.value)}
          >
            <MenuItem value="initiated">Initiated</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="not_needed">Not needed</MenuItem>
          </Select>
        </FormControl>

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
